import React, { Children, ReactNode, createRef, useEffect } from "react";
import { Flipped } from "react-flip-toolkit";
import { DropdownRoot, DropdownBackground, AltBackground } from "./DropDownStyledComponents";
import { InvertedDiv } from "./InvertedDiv";
import HeaderCaret from "@/assets/icons/header/HeaderCaret.svg";
import FadeContents from "./FadeContents";
import clsx from "clsx";
import { Stack } from "@mui/material";

interface IDefaultCaretProps {
	caretClassNames?: string;
}

const DefaultCaret = ({ caretClassNames }: Readonly<IDefaultCaretProps>) => {
	return <img src={HeaderCaret} alt="" className={clsx(caretClassNames, { "z-50": !caretClassNames })} />;
};

const getFirstDropdownSectionHeight = (el: HTMLElement | null) => {
	const offsetHeight = el?.querySelector<HTMLElement>("*[data-first-dropdown-section]")?.offsetHeight;
	if (!offsetHeight) {
		return 0;
	}
	return offsetHeight;
};

interface IUpdateAltBackgroundConfiguration {
	altBackground: HTMLDivElement | null;
	prevDropdown: HTMLElement | null;
	currentDropdown: HTMLElement | null;
	duration: number;
}

const updateAltBackground = ({ altBackground, prevDropdown, currentDropdown }: Readonly<IUpdateAltBackgroundConfiguration>) => {
	const prevHeight = getFirstDropdownSectionHeight(prevDropdown);
	const currentHeight = getFirstDropdownSectionHeight(currentDropdown);

	const immediateSetTranslateY = (el: HTMLElement, translateY: number) => {
		el.style.transform = `translateY(${translateY}px)`;
		el.style.transition = "transform 0s";
		requestAnimationFrame(() => (el.style.transitionDuration = ""));
	};

	if (!altBackground) {
		return;
	}

	if (prevHeight) {
		// transition the grey ("alt") background from its previous height to its current height
		immediateSetTranslateY(altBackground, prevHeight);
		requestAnimationFrame(() => {
			altBackground.style.transform = `translateY(${currentHeight}px)`;
		});
	} else {
		// just immediately set the background to the appropriate height
		// since we don't have a stored value
		immediateSetTranslateY(altBackground, currentHeight);
	}
};

type DropdownContainerProps = {
	children: React.ReactNode;
	direction: "left" | "right";
	duration: number;
	caretPosition?: "start" | "center" | "end";
	caretClassNames?: string;
	CaretSlot?: ReactNode;
};

export default function DropdownContainer(props: Readonly<DropdownContainerProps>) {
	const { children, direction, duration, caretClassNames, caretPosition, CaretSlot } = props;
	const [currentDropdown, prevDropdown] = Children.toArray(children);

	const altBackgroundEl = createRef<HTMLDivElement>();
	const currentDropdownEl = createRef<HTMLDivElement>();
	const prevDropdownEl = createRef<HTMLDivElement>();

	useEffect(() => {
		updateAltBackground({
			altBackground: altBackgroundEl.current,
			prevDropdown: prevDropdownEl.current,
			currentDropdown: currentDropdownEl.current,
			duration,
		});
	}, [altBackgroundEl, currentDropdownEl, duration, prevDropdownEl]);

	if (!currentDropdown) {
		return null;
	}

	return (
		<DropdownRoot $direction={direction} $duration={duration} $caretPosition={caretPosition}>
			<Stack className="absolute -top-[9px] z-10">
				<Flipped flipId="dropdown-caret">
					<FadeContents $direction={direction} $duration={duration}>
						{CaretSlot ?? <DefaultCaret caretClassNames={caretClassNames} />}
					</FadeContents>
				</Flipped>
			</Stack>
			<Flipped flipId="dropdown">
				<DropdownBackground>
					<Flipped inverseFlipId="dropdown">
						<InvertedDiv>
							<AltBackground ref={altBackgroundEl} $duration={duration} />
							<FadeContents $direction={direction} $duration={duration} ref={currentDropdownEl}>
								{currentDropdown}
							</FadeContents>
						</InvertedDiv>
					</Flipped>

					<Flipped inverseFlipId="dropdown" scale>
						<InvertedDiv absolute>
							{prevDropdown && (
								<FadeContents $animatingOut $direction={direction} $duration={duration - 150} ref={prevDropdownEl}>
									{prevDropdown}
								</FadeContents>
							)}
						</InvertedDiv>
					</Flipped>
				</DropdownBackground>
			</Flipped>
		</DropdownRoot>
	);
}
